import React, { useEffect, useRef, useState } from 'react';
import Button from '@components/ui/Button/Button';
import { Modal } from '@components/ui/Modal';
import styles from './IdleWarningModal.module.css';

export interface IIdleWarningModalProps {
  isOpen: boolean;
  countdownSeconds: number;
  onDismiss: () => void;
  onLogout: () => void;
}

const IdleWarningModal: React.FC<IIdleWarningModalProps> = ({
  isOpen,
  countdownSeconds,
  onDismiss,
  onLogout,
}) => {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const stayButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setRemaining(countdownSeconds);
    const interval = setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1_000);
    return () => clearInterval(interval);
  }, [isOpen, countdownSeconds]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onDismiss}
      variant="dialog"
      layer="session-warning"
      backdropClassName={styles.scrim}
      className={styles.dialog}
      initialFocusRef={stayButtonRef}
      aria-labelledby="idle-title"
      data-testid="idle-modal"
    >
      <h2 id="idle-title" className={styles.title}>
        You&rsquo;re about to be signed out
      </h2>
      <p className={styles.body}>
        For your security, we sign you out after a period of inactivity. You&rsquo;ll be signed out
        in{' '}
        <span
          className={styles.countdown}
          data-testid="idle-countdown"
          aria-live="polite"
          aria-atomic="true"
        >
          {remaining}
        </span>{' '}
        second{remaining === 1 ? '' : 's'}.
      </p>
      <div className={styles.buttons}>
        <Button
          type="button"
          variant="secondary"
          size="md"
          className={styles.logOut}
          onClick={onLogout}
          data-testid="idle-logout-btn"
        >
          Log out now
        </Button>
        <Button
          ref={stayButtonRef}
          type="button"
          variant="brand"
          size="md"
          className={styles.staySignedIn}
          onClick={onDismiss}
          data-testid="idle-stay-btn"
        >
          Stay signed in
        </Button>
      </div>
    </Modal>
  );
};

export default IdleWarningModal;
